import { BILL_LANDINGS } from "./bill-landings";
import { buildVideoObjectLd, type VideoConfig } from "./video-embeds";
import { buildUtilitySchemaScripts } from "./utility-schemas";

export const SITE_URL = "https://drhint.com";

export type UtilityAlias = {
  slug: string; // e.g. "check-pesco-bill"
  utilityId: string; // BillCheckerWidget id
  brand: string; // PESCO, K-Electric, etc.
  longName: string; // full provider name for intro text
  sourceLanding?: string; // bill-landings slug to pull faqs/longCopy from
};

export const UTILITY_ALIASES: UtilityAlias[] = [
  { slug: "check-pesco-bill",     utilityId: "pesco", brand: "PESCO",     longName: "Peshawar Electric Supply Company",  sourceLanding: "pesco-bill-check" },
  { slug: "check-lesco-bill",     utilityId: "lesco", brand: "LESCO",     longName: "Lahore Electric Supply Company",     sourceLanding: "check-lesco-bill-online" },
  { slug: "check-fesco-bill",     utilityId: "fesco", brand: "FESCO",     longName: "Faisalabad Electric Supply Company", sourceLanding: "fesco-duplicate-bill-download" },
  { slug: "check-iesco-bill",     utilityId: "iesco", brand: "IESCO",     longName: "Islamabad Electric Supply Company",  sourceLanding: "iesco-bill-by-reference-number" },
  { slug: "check-gepco-bill",     utilityId: "gepco", brand: "GEPCO",     longName: "Gujranwala Electric Power Company",  sourceLanding: "gepco-bill-online" },
  { slug: "check-mepco-bill",     utilityId: "mepco", brand: "MEPCO",     longName: "Multan Electric Power Company",      sourceLanding: "mepco-bill-check-online" },
  { slug: "check-hesco-bill",     utilityId: "hesco", brand: "HESCO",     longName: "Hyderabad Electric Supply Company",  sourceLanding: "hesco-bill-online" },
  { slug: "check-sepco-bill",     utilityId: "sepco", brand: "SEPCO",     longName: "Sukkur Electric Power Company",      sourceLanding: "sepco-bill-online" },
  { slug: "check-qesco-bill",     utilityId: "qesco", brand: "QESCO",     longName: "Quetta Electric Supply Company",     sourceLanding: "qesco-bill-check" },
  { slug: "check-kelectric-bill", utilityId: "ke",    brand: "K-Electric", longName: "K-Electric Karachi" },
  { slug: "check-sngpl-bill",     utilityId: "sngpl", brand: "SNGPL",     longName: "Sui Northern Gas Pipelines",        sourceLanding: "sngpl-gas-bill-checker" },
  { slug: "check-ssgc-bill",      utilityId: "ssgc",  brand: "SSGC",      longName: "Sui Southern Gas Company",          sourceLanding: "ssgc-gas-bill-online" },
  { slug: "check-ptcl-bill",      utilityId: "ptcl",  brand: "PTCL",      longName: "Pakistan Telecommunication Company Limited", sourceLanding: "ptcl-bill-check-online" },
];

export function utilityAliasMeta(alias: UtilityAlias) {
  const title = `Check ${alias.brand} Bill Online - Duplicate ${alias.brand} Bill`;
  const h1 = title;
  const description = `Check ${alias.brand} bill online and download a duplicate ${alias.longName} bill instantly by reference number. Free, secure, mobile-friendly.`;
  const path = `/${alias.slug}`;
  const src = alias.sourceLanding
    ? BILL_LANDINGS.find((b) => b.slug === alias.sourceLanding)
    : undefined;
  return { title, h1, description, path, longCopy: src?.longCopy ?? [], faqs: src?.faqs ?? [], utilityId: alias.utilityId };
}

export type GoldCityAlias = {
  slug: string; // e.g. "gold-rate-lahore"
  country: "pakistan" | "india";
  citySlug: string;
  cityName: string;
};

export const GOLD_CITY_ALIASES: GoldCityAlias[] = [
  { slug: "gold-rate-lahore",  country: "pakistan", citySlug: "lahore",  cityName: "Lahore" },
  { slug: "gold-rate-karachi", country: "pakistan", citySlug: "karachi", cityName: "Karachi" },
  { slug: "gold-rate-mumbai",  country: "india",    citySlug: "mumbai",  cityName: "Mumbai" },
  { slug: "gold-rate-delhi",   country: "india",    citySlug: "delhi",   cityName: "Delhi" },
];

export function goldCityAliasMeta(a: GoldCityAlias) {
  const isIN = a.country === "india";
  const title = isIN
    ? `Today Gold Price in ${a.cityName} — Live INR Rate for NRI / Global Users (Gulf, USA, UK)`
    : `Gold Rate in ${a.cityName} Today — Live PKR Price for Overseas Pakistanis in KSA, UAE, US, UK`;
  const description = isIN
    ? `${a.cityName} me aaj sone ka rate kya hai — live 24K, 22K, 18K per 10 gram, 1 gram, 1 tola. आज ${a.cityName} में सोने का भाव for NRI users in Gulf (KSA, UAE), USA, UK & Bangladesh. Updated every 10 minutes.`
    : `Live gold rate in ${a.cityName} today — 24K, 22K, 21K per tola & per gram. ${a.cityName} me aaj sone ka rate kya hai for overseas Pakistanis in Saudi Arabia, UAE, USA, UK — refreshed every 10 minutes with expat SAR/AED/USD/GBP conversion.`;
  return { title, h1: title, description, path: `/${a.slug}` };
}

export type GoldCountryAlias = { slug: string; country: "pakistan" | "india"; label: string };
export const GOLD_COUNTRY_ALIASES: GoldCountryAlias[] = [
  { slug: "gold-rate-pakistan", country: "pakistan", label: "Pakistan" },
  { slug: "gold-rate-india",    country: "india",    label: "India" },
];

export function goldCountryAliasMeta(a: GoldCountryAlias) {
  const isIN = a.country === "india";
  const title = isIN
    ? `Today Gold Price in India — Live INR Rate for NRI / Global Users (Gulf, USA, UK)`
    : `Gold Rate in Pakistan Today — Live PKR Price for Overseas Pakistanis in KSA, UAE, US, UK`;
  const description = isIN
    ? `India me aaj sone ka rate kya hai — live 24K, 22K, 21K per 10 gram & tola. आज India में सोने का भाव, plus expat SAR/AED/USD/GBP/BDT conversion for NRIs in Gulf, USA, UK & Bangladesh. Updated every 10 minutes.`
    : `Pakistan me aaj sone ka rate kya hai — live 24K, 22K, 21K per tola & gram for overseas Pakistanis in Saudi Arabia, UAE, USA, UK & Bangladesh. SAR/AED/USD/GBP live conversion. Updated every 10 minutes.`;
  return { title, h1: title, description, path: `/${a.slug}` };
}

// Static tool / API aliases (manual mapping, exact titles per spec).
export const STATIC_ALIASES: { slug: string; title: string; description: string; canonicalSource: string }[] = [
  {
    slug: "gold-purity-calculator",
    title: "Gold Purity Calculator - 24K to 22K, 21K, 18K & 14K Converter",
    description: "Free gold purity calculator. Convert any gold weight across 24K, 22K, 21K, 18K and 14K using live international spot rates.",
    canonicalSource: "/tools/gold-purity-converter",
  },
  {
    slug: "jewelry-wastage-calculator",
    title: "Jewelry Wastage Calculator - Making Charges & Wastage Estimator",
    description: "Estimate jewelry wastage and making charges instantly. Live gold price + wastage % + per-gram making fee = final invoice.",
    canonicalSource: "/tools/jewelry-making-estimator",
  },
  {
    slug: "gold-investment-calculator",
    title: "Gold Investment Calculator - Historical ROI & Returns",
    description: "Calculate historical gold investment ROI. See what any past gold purchase would be worth today at the current live spot price.",
    canonicalSource: "/tools/historical-roi-calculator",
  },
  {
    slug: "live-gold-rate-api",
    title: "Free Live Gold Rate API - Real-Time Gold Price JSON Feed",
    description: "Free live gold rate API. Real-time 24K, 22K and 18K spot prices in USD, PKR and INR as a clean JSON REST feed.",
    canonicalSource: "/developer/gold-rate-api",
  },
];

/**
 * The single canonical URL for a city's gold page.
 *
 * A city is reachable at up to three paths — the exact-match alias
 * (/gold-rate-karachi), the short form (/pakistan/karachi) and the long form
 * (/city/pakistan/karachi). They render the same page, so exactly one of them
 * may claim to be the document. Cities that have an exact-match alias keep it
 * (that URL carries the search equity); every other city uses the short form.
 */
export function canonicalCityPath(country: "pakistan" | "india", citySlug: string): string {
  const alias = GOLD_CITY_ALIASES.find((a) => a.country === country && a.citySlug === citySlug);
  return alias ? `/${alias.slug}` : `/${country}/${citySlug}`;
}

export function buildAliasHead(args: {
  title: string;
  description: string;
  path: string;
  video?: VideoConfig;
  faqs?: { q: string; a: string }[];
  utilityId?: string;
}) {
  const pageUrl = `${SITE_URL}${args.path}`;
  // High-res share image used as a sane default for Google Discover / WhatsApp
  // previews on every alias page. Routes can still override og:image in their
  // own head() if a better contextual image becomes available.
  const ogImage =
    "https://storage.googleapis.com/gpt-engineer-file-uploads/H8KExPuR0jRNdSZTB0HQVORkvLq1/social-images/social-1780834751164-IMG_8313.webp";
  return {
    meta: [
      { title: args.title },
      { name: "description", content: args.description },
      // Google Discover eligibility: max-image-preview:large is the single
      // most important signal for Chrome Home Feed inclusion. Reasserted at
      // route level so it survives any future root-level overrides.
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { property: "og:title", content: args.title },
      { property: "og:description", content: args.description },
      { property: "og:url", content: pageUrl },
      { property: "og:type", content: "website" },
      { property: "og:image", content: ogImage },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: args.title },
      { name: "twitter:description", content: args.description },
      { name: "twitter:image", content: ogImage },
    ],
    // Self-referencing canonical shield: alias canonicalizes to itself, NOT the parent.
    links: [{ rel: "canonical", href: pageUrl }],
    scripts: (() => {
      const s: Array<{ type: "application/ld+json"; children: string }> = [];
      if (args.video) s.push(buildVideoObjectLd({ video: args.video, pageUrl }));
      if (args.faqs && args.faqs.length > 0) {
        s.push({
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: args.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        });
      }
      for (const x of buildUtilitySchemaScripts(args.utilityId, pageUrl)) s.push(x);
      return s.length > 0 ? s : undefined;
    })(),
  };
}